import React, { useEffect, useState } from 'react';
import { clearPlans } from 'State/Plans';
import { getEntitlements } from 'State/User';
import { useStore } from 'Hooks/useStore';
import { classCombine } from 'Utils/Utils';
import Cookies from 'universal-cookie';
import { trackEvent, TRACKING_ENUMS } from 'Utils/Analytics';
import { fetchMobileRegcode } from 'Api/ProfileApi';
import { setLocation } from 'Utils/Path';
import { useBeforeUnload } from 'react-router-dom';
import styles from './FoxOneConfirmation.module.css';
import { registerSecondScreen } from 'State/SecondScreen';
import { sessionStorageRemove } from 'Utils/Storage';
import { Logo } from 'Components/Logo';
import { isIOS, isAndroid } from 'react-device-detect';
import AppsFlyerLoader from 'Components/AppsFlyerLoader';
import { APPSFLYER_LINK_ELEMENT_ID } from 'Components/AppsFlyerLoader/getScript';

const cookies = new Cookies();

const DEVICES = {
  IOS: {
    ALT: 'iOS',
    IMG: '/account/static/images/confirmation/iOS.svg',
  },
  ANDROID: {
    ALT: 'Android',
    IMG: '/account/static/images/confirmation/Android.svg',
  },
  ROKU: {
    ALT: 'Roku',
    IMG: '/account/static/images/confirmation/Roku.svg',
  },
  APPLE_TV: {
    ALT: 'Apple TV',
    IMG: '/account/static/images/confirmation/AppleTV.svg',
  },
  FIRE_TV: {
    ALT: 'Fire TV',
    IMG: '/account/static/images/confirmation/FireTV.svg',
  },
  GOOGLE_TV: {
    ALT: 'Google TV',
    IMG: '/account/static/images/confirmation/GoogleTV.svg',
  },
  LG_TV: {
    ALT: 'LG TV',
    IMG: '/account/static/images/confirmation/LGTV.svg',
  },
  SAMSUNG: {
    ALT: 'Samsung Smart TV',
    IMG: '/account/static/images/confirmation/Samsung.svg',
  },
  VIZIO: {
    ALT: 'Vizio',
    IMG: '/account/static/images/confirmation/Vizio.svg',
  },
  ANDROID_TV: {
    ALT: 'Android TV',
    IMG: '/account/static/images/confirmation/AndroidTV.svg',
  },
  XBOX: {
    ALT: 'Xbox',
    IMG: '/account/static/images/confirmation/Xbox.svg',
  },
};

export function FoxOneConfirmation() {
  const { hasEntitlements } = useStore('user');
  const { defaultConfig } = useStore('appConfig');
  const { deviceIdCookieName, successOneLinkURL } = defaultConfig;
  const { isDeviceActivation } = useStore('secondScreen');
  const [regcode, setRegcode] = useState('');

  const BUTTON_TEXT = 'Get Mobile App';

  useEffect(() => {
    const retrieveRegcode = async (deviceId) => {
      const { code } = await fetchMobileRegcode(deviceId);
      setRegcode(code);
    };
    const deviceId = cookies.get(deviceIdCookieName);
    retrieveRegcode(deviceId);
  }, [deviceIdCookieName]);

  const openApp = (e) => {
    e.preventDefault();

    trackEvent(
      TRACKING_ENUMS.EVENT_DEVICE_LINK_BUTTON_CLICKED,
      {
        page_item_title: 'signup confirmation',
        page_ui_element: 'button',
      },
      {
        item_title: 'signup confirmation',
        button_text: BUTTON_TEXT,
        ui_element: 'button',
      }
    );

    const el = document.getElementById(APPSFLYER_LINK_ELEMENT_ID);
    if (el) {
      let href = el.getAttribute('data-href');
      if (href && regcode) {
        const separator = href.includes('?') ? '&' : '?';
        href = `${href}${separator}deep_link_value_sub1=${regcode}`;
      }

      setLocation(href, 'assign');
    }
  };

  // Remove plan id from session storage, only needed during this screen for Heap integration
  // https://teamfox.atlassian.net/browse/NATCOM-1511
  useBeforeUnload(() => sessionStorageRemove('purchased_plan_id'));

  useEffect(() => {
    /* Clearing sign up plans so that if go to change plans it will fetch appropriate packages */
    getEntitlements(1);
    clearPlans();
  }, []);

  useEffect(() => {
    if (isDeviceActivation && hasEntitlements) {
      registerSecondScreen();
    }
  }, [isDeviceActivation, hasEntitlements]);

  const renderIcon = (deviceName) => {
    const device = DEVICES[deviceName];
    if (!device) return null;

    return (
      <div className={styles.deviceIcon} data-id-icon={deviceName}>
        <img className={styles.deviceIconImg} alt={device.ALT} src={device.IMG} />
      </div>
    );
  };

  return (
    <div className={styles.mainContainer}>
      <div className={styles.confirmationHeader}>
        <div className={styles.roundedHeaderContainer}>
          <div className={styles.roundedHeader} />
        </div>
        <div className={styles.headerContent}>
          <div className={styles.logoContainer}>
            <Logo className={styles.logo} />
            <Logo className={styles.mobileLogo} isVariant width={112} height={37} />
          </div>
          <img
            className={styles.successCheckmark}
            src="/account/static/images/white-checkbox-filled.svg"
            alt="Check mark"
          />
          <h1 className={styles.pageTitle}>Purchase Confirmed!</h1>
          <h1 className={styles.pageSubTitle}>Thank you for subscribing to FOX Nation</h1>
        </div>
      </div>
      <div className={styles.mainContent}>
        <h1 className={styles.mainContentTitle}>Important Information</h1>
        <h1 className={styles.mainContentSubTitle}>Download FOX One to watch FOX Nation</h1>
        <div className={styles.logoFlowChart}>
          <img
            className={styles.foxNationLogo}
            src="/account/static/images/FN/logo-fox-nation.svg"
            alt="FOX Nation Logo"
          />
          <img
            className={styles.rightArrow}
            src="/account/static/images/FN/success-right-arrow.svg"
            alt="Right arrow"
          />
          <img className={styles.foxOneLogo} src="/account/static/images/FN/fox-one-logo.svg" alt="FOX One Logo" />
        </div>
        <div className={classCombine(styles.downloadStep, styles.step1)}>
          <div className={styles.downloadStepContent}>
            <h1 className={styles.downloadStepNumber}>1</h1>
            <div>
              <h1 className={classCombine(styles.downloadStepTitle, styles.desktopText)}>Download the FOX One App</h1>
              <h1 className={classCombine(styles.downloadStepTitle, styles.mobileText)}>
                Download the FOX One App here
              </h1>

              <>
                {!isAndroid ? (
                  <a id={APPSFLYER_LINK_ELEMENT_ID} onClick={openApp}>
                    <img
                      className={styles.mobileDownloadBtn}
                      src="/account/static/images/confirmation/apple-store-download.svg"
                      alt="Apple Store Download"
                    />
                  </a>
                ) : (
                  <></>
                )}
                {!isIOS ? (
                  <a id={APPSFLYER_LINK_ELEMENT_ID} onClick={openApp}>
                    <img
                      className={styles.mobileDownloadBtn}
                      src="/account/static/images/confirmation/google-play-download.svg"
                      alt="Google Play Download"
                    />
                  </a>
                ) : (
                  <></>
                )}
              </>
            </div>
          </div>
          <div className={styles.downloadStepIcon}>
            <img
              className={styles.downloadStepIconImg}
              src="/account/static/images/FN/download-icon.svg"
              alt="Download icon"
            />
          </div>
        </div>
        <div className={classCombine(styles.downloadStep, styles.step2)}>
          <div className={styles.downloadStepContent}>
            <h1 className={styles.downloadStepNumber}>2</h1>
            <h1 className={styles.downloadStepTitle}>Sign In using your FOX Nation email and password.</h1>
          </div>
          <div className={styles.downloadStepIcon}>
            <img className={styles.downloadStepIconImg} src="/account/static/images/FN/user-icon.svg" alt="User icon" />
          </div>
        </div>
        <div className={classCombine(styles.downloadStep, styles.step3)}>
          <div className={styles.downloadStepContent}>
            <h1 className={styles.downloadStepNumber}>3</h1>
            <h1 className={classCombine(styles.downloadStepTitle, styles.desktopText)}>
              Start Watching! Enjoy your FOX Nation content
            </h1>
            <h1 className={classCombine(styles.downloadStepTitle, styles.mobileText)}>
              Start Watching!
              <br />
              Enjoy your FOX Nation content
            </h1>
          </div>
          <div className={styles.downloadStepIcon}>
            <img className={styles.downloadStepIconImg} src="/account/static/images/FN/play-icon.svg" alt="Play icon" />
          </div>
        </div>
        <div className={styles.availableDevicesContainer}>
          <h1>FOX One is available on these devices</h1>
          <div className={styles.deviceIconContainer}>
            <div className={classCombine(styles.deviceRow, styles.firstRow)}>
              {renderIcon('APPLE_TV')}
              {renderIcon('IOS')}
              {renderIcon('FIRE_TV')}
              {renderIcon('ROKU')}
              {renderIcon('ANDROID')}
            </div>
            <div className={classCombine(styles.deviceRow, styles.secondRow)}>
              {renderIcon('SAMSUNG')}
              {renderIcon('VIZIO')}
              {renderIcon('GOOGLE_TV')}
              {renderIcon('ANDROID_TV')}
            </div>
            <div className={classCombine(styles.deviceRow, styles.thirdRow)}>
              {renderIcon('LG_TV')}
              {renderIcon('XBOX')}
            </div>
          </div>
        </div>
      </div>
      <AppsFlyerLoader oneLinkURL={successOneLinkURL} />
    </div>
  );
}
